import { FALLBACK_PLAYERS } from "../constants/upDownConstants";
import { randomInt, clampStake, clamp, roundToCents } from "./upDownFormatters";

export function shuffle(list) {
  const copy = [...list];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    const cached = copy[index];
    copy[index] = copy[randomIndex];
    copy[randomIndex] = cached;
  }
  return copy;
}

export function pickRandomPlayerName(existingBets) {
  const usedNames = new Set(existingBets.map((bet) => bet.name));
  const available = FALLBACK_PLAYERS.filter((name) => !usedNames.has(name));
  if (available.length > 0) {
    return available[randomInt(0, available.length - 1)];
  }
  return FALLBACK_PLAYERS[randomInt(0, FALLBACK_PLAYERS.length - 1)];
}

export function createSideBets(side, count, minStake, maxStake, roundIndex) {
  const pool = shuffle(FALLBACK_PLAYERS.slice());
  const list = [];

  for (let index = 0; index < count; index += 1) {
    const name = pool[index % pool.length];
    const amount = roundToCents(
      minStake + Math.random() * Math.max(1, maxStake - minStake),
    );
    list.push({
      id: `house-${side}-${roundIndex}-${index}`,
      side,
      name,
      amount,
      isUser: false,
    });
  }

  return list;
}

export function createSmoothSideBets(
  side,
  previousSideBets,
  count,
  minStake,
  maxStake,
  roundIndex,
) {
  const list = [];
  const namePool = shuffle(FALLBACK_PLAYERS.slice());

  for (let index = 0; index < count; index += 1) {
    const source =
      previousSideBets[index % Math.max(previousSideBets.length, 1)];
    const baseAmount =
      source?.amount ??
      minStake + Math.random() * Math.max(1, maxStake - minStake);
    const swing =
      (Math.random() - 0.5) * Math.max(0.9, (maxStake - minStake) * 0.06);
    const amount = clampStake(baseAmount + swing, minStake, maxStake);

    list.push({
      id: `house-${side}-${roundIndex}-${index}`,
      side,
      name: source?.name ?? namePool[index % namePool.length],
      amount,
      isUser: false,
    });
  }

  return list;
}

export function buildHouseBets(variant, roundIndex) {
  const min = variant.minimumStake;
  const max = variant.maximumStake;

  const upCount = randomInt(1, 4);
  const downCount = randomInt(1, 4);

  const upBets = createSideBets("up", upCount, min, max, roundIndex);
  const downBets = createSideBets("down", downCount, min, max, roundIndex);

  return [...upBets, ...downBets];
}

export function buildNextRoundHouseBets(previousBets, variant, roundIndex) {
  const previousHouse = previousBets.filter((bet) => !bet.isUser);
  if (previousHouse.length === 0) {
    return buildHouseBets(variant, roundIndex);
  }

  const min = variant.minimumStake;
  const max = variant.maximumStake;
  const upPrev = previousHouse.filter((bet) => bet.side === "up");
  const downPrev = previousHouse.filter((bet) => bet.side === "down");

  const upCount = clamp(upPrev.length + randomInt(-1, 1), 1, 4);
  const downCount = clamp(downPrev.length + randomInt(-1, 1), 1, 4);

  const upBets = createSmoothSideBets(
    "up",
    upPrev,
    upCount,
    min,
    max,
    roundIndex,
  );
  const downBets = createSmoothSideBets(
    "down",
    downPrev,
    downCount,
    min,
    max,
    roundIndex,
  );
  return [...upBets, ...downBets];
}

export function buildJoinSequence(houseBets) {
  const upQueue = shuffle(houseBets.filter((bet) => bet.side === "up"));
  const downQueue = shuffle(houseBets.filter((bet) => bet.side === "down"));
  const sequence = [];

  while (upQueue.length > 0 || downQueue.length > 0) {
    if (upQueue.length > 0) {
      sequence.push(upQueue.shift());
    }
    if (downQueue.length > 0) {
      sequence.push(downQueue.shift());
    }
  }

  return sequence;
}

export function buildTradePanelParticipants({
  side,
  amount,
  minimumStake,
  maximumStake,
  orderId,
}) {
  const usedNames = new Set(["You"]);
  const participants = [
    {
      id: `panel-user-${orderId}`,
      side,
      name: "You",
      amount,
      isUser: true,
      isPendingUserBid: true,
    },
  ];

  for (const entrySide of ["up", "down"]) {
    const count = randomInt(
      entrySide === side ? 3 : 2,
      entrySide === side ? 5 : 4,
    );
    for (let index = 0; index < count; index += 1) {
      const availableNames = FALLBACK_PLAYERS.filter(
        (item) => !usedNames.has(item),
      );
      const name =
        availableNames[index % Math.max(availableNames.length, 1)] ||
        FALLBACK_PLAYERS[index % FALLBACK_PLAYERS.length];
      usedNames.add(name);
      const baseAmount =
        entrySide === side
          ? amount * (0.45 + Math.random() * 1.15)
          : minimumStake +
            Math.random() * Math.max(1, maximumStake - minimumStake);
      participants.push({
        id: `panel-${entrySide}-${orderId}-${index}`,
        side: entrySide,
        name,
        amount: clampStake(baseAmount, minimumStake, maximumStake),
        isUser: false,
        isServerBid: true,
      });
    }
  }

  return participants;
}

export function evolveHouseBets(previousBets, variant, roundIndex) {
  const userBets = previousBets.filter((bet) => bet.isUser);
  const houseBets = previousBets.filter((bet) => !bet.isUser);
  const minimumStake = variant.minimumStake;
  const maximumStake = variant.maximumStake;

  if (houseBets.length === 0) {
    return previousBets;
  }

  const shouldAddNew = houseBets.length < 12 && Math.random() < 0.26;
  if (shouldAddNew) {
    const nextSide = Math.random() > 0.5 ? "up" : "down";
    houseBets.push({
      id: `house-live-${nextSide}-${roundIndex}-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
      side: nextSide,
      name: pickRandomPlayerName(houseBets),
      amount: roundToCents(
        minimumStake + Math.random() * Math.max(1, maximumStake - minimumStake),
      ),
      isUser: false,
    });
  } else {
    const pickIndex = randomInt(0, houseBets.length - 1);
    const target = houseBets[pickIndex];
    const swing =
      (Math.random() - 0.5) *
      Math.max(0.8, (maximumStake - minimumStake) * 0.08);
    houseBets[pickIndex] = {
      ...target,
      amount: clampStake(target.amount + swing, minimumStake, maximumStake),
    };
  }

  return [...houseBets.slice(-14), ...userBets];
}

export function resolveWinnerSide(startPrice, endPrice) {
  if (endPrice > startPrice) {
    return "up";
  }
  if (endPrice < startPrice) {
    return "down";
  }
  return "draw";
}

